// V25.7 — Month-by-month data completeness.
//
// Why this exists: SMJ's Aug-2026 package printed July and August as blank
// columns. Both QBO pulls (the 8-month YTD P&L and the 12-month monthly P&L)
// agreed to $2, i.e. QuickBooks genuinely held no revenue for those months —
// the write-up simply hadn't been done. Nothing in the app could surface that,
// so it read as a bug for two weeks. This module reads the TTM payload the
// dashboard already has (no extra QBO calls) and names any month at the tail of
// the reporting window that QuickBooks returned empty or near-empty.

const BUCKETS = ['revenue', 'cogs', 'opex', 'other_income', 'other_expense'];

const MONTH_FULL = {
  Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April',
  May: 'May', Jun: 'June', Jul: 'July', Aug: 'August',
  Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};

export function formatDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * @param ttmData  the `/api/qbo/ttm/{client_id}` payload
 * @returns null when there is nothing to judge, otherwise
 *   { rows, typicalLineCount, lastFullyPosted, trailingIssues, hasIssues }
 */
export function analyzeMonthCompleteness(ttmData) {
  const months = ttmData?.months || [];
  if (months.length < 2) return null;
  const detail = ttmData?.detail_lines_monthly || [];

  const rows = months.map((m, i) => {
    const activity = BUCKETS.reduce((s, k) => s + Math.abs(m[k] || 0), 0);
    const postedLines = detail.reduce(
      (n, d) => n + (Math.abs(d.months?.[i] || 0) >= 0.005 ? 1 : 0), 0);
    return {
      index: i,
      label: `${MONTH_FULL[m.month] || m.month} ${m.year}`,
      shortLabel: `${m.month} ${String(m.year).slice(-2)}`,
      start: m.start,
      end: m.end,
      revenue: m.revenue || 0,
      activity,
      postedLines,
      status: 'ok',
      note: '',
    };
  });

  const counts = rows.map((r) => r.postedLines).filter((n) => n > 0).sort((a, b) => a - b);
  const typicalLineCount = counts.length ? counts[Math.floor(counts.length / 2)] : 0;

  rows.forEach((r) => {
    if (r.activity < 0.5 && r.postedLines === 0) {
      r.status = 'empty';
      r.note = 'no activity posted in QuickBooks at all';
    } else if (Math.abs(r.revenue) < 0.5) {
      r.status = 'sparse';
      r.note = `$0 revenue, and only ${r.postedLines} expense line${r.postedLines === 1 ? '' : 's'} posted`;
    } else if (typicalLineCount > 0 && r.postedLines < typicalLineCount * 0.5) {
      r.status = 'sparse';
      r.note = `only ${r.postedLines} of a typical ${typicalLineCount} lines posted`;
    }
  });

  // Contiguous run of problem months at the END of the window — that's the
  // only run that can silently understate the statements being issued.
  const trailingIssues = [];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].status === 'ok') break;
    trailingIssues.unshift(rows[i]);
  }

  let lastFullyPosted = null;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].status === 'ok') { lastFullyPosted = rows[i]; break; }
  }

  return {
    rows,
    typicalLineCount,
    lastFullyPosted,
    trailingIssues,
    hasIssues: trailingIssues.length > 0,
  };
}
